import { ApolloError } from "@apollo/client";
import {
  Box,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import Papa from "papaparse";
import React, { useCallback, useState } from "react";
import { useFieldArray, useForm, UseFormRegister } from "react-hook-form";
import { BsPlusCircle } from "react-icons/bs";
import { MdDelete, MdEmail } from "react-icons/md";
import readXlsxFile from "read-excel-file";

import Alert from "../Alert";
import { Button } from "../Buttons";
import { Link } from "../Link";
import { useToast } from "../useToast";
import type { InviteUserInput, InviteUsersCallback } from "./types";

interface FormUser {
  email?: string;
  userRoleId?: string;
  planUserRoleId?: string | null;
  aiInterviewerUserRoleId?: string | null;
}

interface FormValues {
  users?: FormUser[];
}

interface Role {
  id: string;
  name: string;
  permissions: string[];
  formattedName?: string | null;
  formattedPermissions?: string | null;
}

interface InviteUsersButtonProps<T extends InviteUserInput = InviteUserInput> {
  inviteUsers: InviteUsersCallback<T>;
  roles: Role[];
  includePlanRoleField?: boolean;
  planRoles: Role[];
  includeScreenRoleField?: boolean;
  screenRoles: Role[];
  disabled?: boolean;
  maxInvites?: number;
  initialIsOpen?: boolean;
}

const MAX_USERS_PER_UPLOAD = 100;
const MAX_USERS_PER_UPLOAD_MESSAGE = `Max ${MAX_USERS_PER_UPLOAD} users per upload`;

const InviteUsersButton = <T extends InviteUserInput = InviteUserInput>({
  inviteUsers,
  roles,
  includePlanRoleField = false,
  planRoles,
  includeScreenRoleField = false,
  screenRoles = [],
  disabled = false,
  maxInvites = 5,
  initialIsOpen = false,
}: InviteUsersButtonProps<T>): React.ReactElement => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      users: [
        {
          email: "",
          userRoleId: roles[0]?.id ?? "",
          planUserRoleId: planRoles[0]?.id ?? "",
          aiInterviewerUserRoleId: screenRoles[0]?.id ?? null,
        },
      ],
    },
  });
  const { append, remove, fields } = useFieldArray({
    control,
    name: "users",
  });

  const submitInviteUser = handleSubmit(
    async (values: FormValues): Promise<void> => {
      const validUsers = values.users?.filter(
        (
          user
        ): user is {
          email: string;
          userRoleId: string;
          planUserRoleId?: string | null;
          aiInterviewerUserRoleId?: string | null;
        } => !!user.email
      );
      if (validUsers?.length) {
        if (validUsers.length > MAX_USERS_PER_UPLOAD) {
          toast({
            title: "Error",
            description: MAX_USERS_PER_UPLOAD_MESSAGE,
            status: "error",
          });
          return;
        }
        setError(null);
        setIsLoading(true);
        try {
          // Type assertion: FormUser is a subset of InviteUserInput
          // The callback will validate the full type at the call site
          await inviteUsers(validUsers as T[]);
          setIsLoading(false);
          setIsOpen(false);
          reset();
        } catch (error) {
          setIsLoading(false);
          setError(error);
        }
      }
    }
  );

  const updateUsers = (
    users: {
      email: string;
      role: string;
      planRole: string;
      screenRole?: string;
    }[]
  ): void => {
    const roleMap = roles.reduce((acc, role): { [key: string]: string } => {
      acc[role.name.toLowerCase()] = role.id;
      return acc;
    }, {} as { [key: string]: string });
    const planRoleMap = planRoles.reduce(
      (acc, role): { [key: string]: string } => {
        acc[role.name.toLowerCase()] = role.id;
        return acc;
      },
      {} as { [key: string]: string }
    );
    const screenRoleMap = screenRoles.reduce(
      (acc, role): { [key: string]: string } => {
        acc[role.name.toLowerCase()] = role.id;
        return acc;
      },
      {} as { [key: string]: string }
    );
    // Remove all existing fields before appending new ones
    if (fields.length > 0) {
      remove();
    }
    users.forEach((user, idx) => {
      const roleId = roleMap[user.role.toLowerCase()];
      const planRoleId = planRoleMap[user.planRole.toLowerCase()];
      const screenRoleId = user.screenRole?.trim()
        ? screenRoleMap[user.screenRole.toLowerCase().trim()]
        : "";
      append({
        email: user.email,
        userRoleId: roleId,
        planUserRoleId: planRoleId,
        aiInterviewerUserRoleId: screenRoleId || null,
      });
    });
  };

  const parseFileRows = (
    rows: string[][]
  ): {
    email: string;
    role: string;
    planRole: string;
    screenRole?: string;
  }[] => {
    if (rows.length === 0) {
      return [];
    }

    // Find column indices by header names (case-insensitive)
    const headerRow = rows[0].map((cell) => String(cell).toLowerCase().trim());
    const emailCol = headerRow.findIndex(
      (h) => h === "email" || h === "e-mail"
    );
    const interviewRoleCol = headerRow.findIndex(
      (h) =>
        h === "interview role" ||
        h === "role" ||
        h === "user role" ||
        h === "interviewrole"
    );
    const planRoleCol = headerRow.findIndex(
      (h) =>
        h === "plan role" ||
        h === "planrole" ||
        (includePlanRoleField && h.includes("plan"))
    );
    const screenRoleCol = headerRow.findIndex(
      (h) =>
        h === "screen role" ||
        h === "screenrole" ||
        (includeScreenRoleField && h.includes("screen"))
    );

    if (emailCol === -1 || interviewRoleCol === -1) {
      throw new Error(
        "File must contain 'Email' and 'Interview Role' (or 'Role') columns"
      );
    }

    const parsedRows = rows
      .slice(1)
      .map((row) => {
        const email = String(row[emailCol] || "").trim();
        const role = String(row[interviewRoleCol] || "").trim();
        const planRole =
          planRoleCol !== -1 ? String(row[planRoleCol] || "").trim() : "";
        const screenRole =
          screenRoleCol !== -1 ? String(row[screenRoleCol] || "").trim() : "";

        return {
          email,
          role,
          planRole,
          screenRole,
        };
      })
      .filter((row) => row.email);

    if (parsedRows.length > MAX_USERS_PER_UPLOAD) {
      throw new Error(MAX_USERS_PER_UPLOAD_MESSAGE);
    }

    return parsedRows;
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e?.target?.files?.[0];
    if (!file) {
      return;
    }
    const filename = e.target.value;
    if (filename.endsWith(".csv")) {
      new Promise((resolve) => {
        Papa.parse(file, {
          worker: true,
          complete: (results: { data: string[][] }): void => {
            resolve(results.data);
          },
        });
      })
        .then((rows: unknown): void => {
          try {
            const parsedRows = parseFileRows(rows as string[][]);
            updateUsers(parsedRows);
          } catch (error) {
            toast({
              title: "Error",
              description:
                error instanceof Error
                  ? error.message
                  : `Error reading file. Did you use the sample file?`,
              status: "error",
            });
          }
        })
        .catch(() => {
          toast({
            title: "Error",
            description: `Error reading file. Did you use the sample file?`,
            status: "error",
          });
        });
    } else if (filename.endsWith(".xls") || filename.endsWith(".xlsx")) {
      readXlsxFile(file)
        .then((rows) => {
          try {
            const parsedRows = parseFileRows(
              rows.map((row) => row.map((cell) => String(cell)))
            );
            updateUsers(parsedRows);
          } catch (error) {
            toast({
              title: "Error",
              description:
                error instanceof Error
                  ? error.message
                  : `Error reading file. Did you use the sample file?`,
              status: "error",
            });
          }
        })
        .catch(() => {
          toast({
            title: "Error",
            description: `Error reading file. Did you use the sample file?`,
            status: "error",
          });
        });
    } else {
      toast({
        title: "Error",
        description: `Invalid file format. Must be a CSV or Excel file.`,
        status: "error",
      });
    }
  };

  const interviewRoleFieldName = includePlanRoleField
    ? "Interview Role"
    : "Role";

  const generateTemplateFile = useCallback((): void => {
    // Build headers based on enabled fields
    const headers: string[] = ["Email", interviewRoleFieldName];
    if (includePlanRoleField) {
      headers.push("Plan Role");
    }
    if (includeScreenRoleField) {
      headers.push("Screen Role");
    }

    // Generate example rows showing all role values
    const exampleRows: string[][] = [];

    // Create rows to show all interview roles, and distribute plan/screen roles across them
    roles.forEach((role, roleIndex) => {
      const email = `example${roleIndex + 1}@email.com`;
      const row: string[] = [email, role.name];

      if (includePlanRoleField) {
        // Cycle through plan roles to show all values
        const planRoleIndex = roleIndex % planRoles.length;
        row.push(planRoles[planRoleIndex]?.name || "");
      }

      if (includeScreenRoleField) {
        // Cycle through screen roles to show all values
        const screenRoleIndex = roleIndex % screenRoles.length;
        row.push(screenRoles[screenRoleIndex]?.name || "");
      }

      exampleRows.push(row);
    });

    // If we have plan roles but fewer interview roles, add more rows to show all plan roles
    if (includePlanRoleField && planRoles.length > roles.length) {
      const additionalRowsNeeded = planRoles.length - roles.length;
      for (let i = 0; i < additionalRowsNeeded; i++) {
        const rowIndex = roles.length + i;
        const email = `example${rowIndex + 1}@email.com`;
        const row: string[] = [
          email,
          roles[0]?.name || "", // Use first interview role
          planRoles[rowIndex]?.name || "",
        ];

        if (includeScreenRoleField) {
          const screenRoleIndex = rowIndex % screenRoles.length;
          row.push(screenRoles[screenRoleIndex]?.name || "");
        }

        exampleRows.push(row);
      }
    }

    // If we have screen roles but fewer rows than screen roles, add more rows
    if (includeScreenRoleField && screenRoles.length > exampleRows.length) {
      const additionalRowsNeeded = screenRoles.length - exampleRows.length;
      for (let i = 0; i < additionalRowsNeeded; i++) {
        const rowIndex = exampleRows.length;
        const email = `example${rowIndex + 1}@email.com`;
        const row: string[] = [
          email,
          roles[0]?.name || "", // Use first interview role
        ];

        if (includePlanRoleField) {
          const planRoleIndex = rowIndex % planRoles.length;
          row.push(planRoles[planRoleIndex]?.name || "");
        }

        row.push(screenRoles[rowIndex]?.name || "");
        exampleRows.push(row);
      }
    }

    while (exampleRows.length < 4) {
      exampleRows.push(Array(headers.length).fill(""));
    }

    const limitMessageRow = Array(headers.length).fill("");
    limitMessageRow[1] = MAX_USERS_PER_UPLOAD_MESSAGE;
    exampleRows.splice(4, 0, limitMessageRow);

    // Generate CSV content
    const csvContent = Papa.unparse([headers, ...exampleRows]);

    // Create blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "BrightHireUserInviteTemplate.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [
    includePlanRoleField,
    includeScreenRoleField,
    interviewRoleFieldName,
    roles,
    planRoles,
    screenRoles,
  ]);

  return (
    <>
      <Button
        data-testid="invite-users"
        size="sm"
        leftIcon={<MdEmail />}
        disabled={roles.length < 1 || disabled}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        Invite users
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        closeOnOverlayClick={false}
        size="xl"
      >
        <ModalOverlay>
          <ModalContent
            maxW={
              includePlanRoleField || includeScreenRoleField
                ? "900px"
                : undefined
            }
          >
            <ModalHeader>Invite Users</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <form id="invite-users-form" onSubmit={submitInviteUser}>
                {error && <Alert status="error" description={error.message} />}
                <Box overflowX="auto">
                  <Box display="table" minW="100%">
                    <Box display="table-row" key="header" height="2.2rem">
                      <FormControl display="table-cell">
                        <FormLabel htmlFor="email">Email</FormLabel>
                      </FormControl>
                      <FormControl id="userRoleId" display="table-cell">
                        <FormLabel>{interviewRoleFieldName}</FormLabel>
                      </FormControl>
                      <FormControl
                        id="planUserRoleId"
                        display="table-cell"
                        hidden={!includePlanRoleField}
                      >
                        <FormLabel>Plan Role</FormLabel>
                      </FormControl>
                      <FormControl
                        id="aiInterviewerUserRoleId"
                        display="table-cell"
                        hidden={!includeScreenRoleField}
                      >
                        <FormLabel>Screen Role</FormLabel>
                      </FormControl>
                      <FormControl display="table-cell">
                        <FormLabel>Remove</FormLabel>
                      </FormControl>
                    </Box>
                    {fields.map((field, idx) => (
                      <UserRow
                        key={field.id}
                        idx={idx}
                        user={field}
                        roles={roles}
                        includePlanRoleField={includePlanRoleField}
                        planRoles={planRoles}
                        includeScreenRoleField={includeScreenRoleField}
                        screenRoles={screenRoles}
                        register={register}
                        remove={remove}
                        isDisabled={fields.length < 2}
                        emailError={errors?.users?.[idx]?.email?.message}
                      />
                    ))}
                  </Box>
                </Box>
                <Button
                  variant="outline"
                  size="sm"
                  width="200px"
                  leftIcon={<BsPlusCircle />}
                  onClick={() => {
                    const users = getValues()?.users;
                    const userRoleId =
                      users && users.length > 0
                        ? users[users.length - 1].userRoleId
                        : "";
                    const planUserRoleId =
                      users && users.length > 0
                        ? users[users.length - 1].planUserRoleId
                        : "";
                    const aiInterviewerUserRoleId =
                      users && users.length > 0
                        ? users[users.length - 1].aiInterviewerUserRoleId
                        : "";
                    append({
                      email: "",
                      userRoleId,
                      planUserRoleId,
                      aiInterviewerUserRoleId,
                    });
                  }}
                  isDisabled={fields.length === maxInvites}
                >
                  Add another
                </Button>
              </form>
            </ModalBody>
            <ModalFooter>
              <Box mr="auto" fontSize="xs">
                <Text mb={1}>
                  or upload a file{" "}
                  <Link
                    as="button"
                    onClick={generateTemplateFile}
                    textDecoration="underline"
                    cursor="pointer"
                  >
                    like this
                  </Link>
                  {` (${MAX_USERS_PER_UPLOAD_MESSAGE.toLowerCase()})`}
                </Text>
                <input
                  type="file"
                  id="input"
                  placeholder="Upload"
                  onChange={onUpload}
                />
              </Box>
              <ButtonGroup>
                <Button
                  variant="outline"
                  mr={3}
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  type="submit"
                  form="invite-users-form"
                  data-testid="invite"
                  isLoading={isLoading}
                >
                  Invite
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

const UserRow: React.FC<{
  user: FormUser;
  idx: number;
  roles: Role[];
  includePlanRoleField?: boolean;
  planRoles: Role[];
  includeScreenRoleField?: boolean;
  screenRoles: Role[];
  emailError?: string;
  register: UseFormRegister<FormValues>;
  isDisabled: boolean;
  remove: (idx: number) => void;
}> = ({
  user,
  idx,
  roles,
  includePlanRoleField,
  planRoles,
  includeScreenRoleField,
  screenRoles,
  register,
  remove,
  emailError,
  isDisabled,
}) => {
  const role = roles.find((r) => user.userRoleId === r.id);
  const planRole = planRoles.find((r) => user.planUserRoleId === r.id);
  const screenRole = screenRoles.find(
    (r) => user.aiInterviewerUserRoleId === r.id
  );

  return (
    <Box display="table-row">
      <FormControl
        isRequired
        isInvalid={emailError !== undefined}
        display="table-cell"
        h="50px"
      >
        <Input
          data-testid={`email[${idx}]`}
          type="email"
          size="sm"
          width="200px"
          {...register(`users.${idx}.email`, {
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          defaultValue={user.email}
        />
        {emailError !== undefined && (
          <FormErrorMessage my="1">{emailError}</FormErrorMessage>
        )}
      </FormControl>
      <Tooltip label={role?.formattedPermissions ?? undefined} openDelay={250}>
        <FormControl isRequired display="table-cell" pr={4}>
          <Select
            {...register(`users.${idx}.userRoleId`)}
            size="sm"
            data-testid={`userRoleId[${idx}]`}
            w="max-content"
            minW="112px"
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.formattedName}
              </option>
            ))}
          </Select>
        </FormControl>
      </Tooltip>
      {includePlanRoleField && (
        <Tooltip
          label={planRole?.formattedPermissions ?? undefined}
          openDelay={250}
        >
          <FormControl display="table-cell" pr={4}>
            <Select
              {...register(`users.${idx}.planUserRoleId`)}
              size="sm"
              data-testid={`planUserRoleId[${idx}]`}
              w="max-content"
              minW="112px"
            >
              {planRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.formattedName}
                </option>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
      )}
      {includeScreenRoleField && (
        <Tooltip
          label={screenRole?.formattedPermissions ?? undefined}
          openDelay={250}
        >
          <FormControl display="table-cell" pr={4}>
            <Select
              {...register(`users.${idx}.aiInterviewerUserRoleId`)}
              size="sm"
              data-testid={`aiInterviewerUserRoleId[${idx}]`}
              w="max-content"
              minW="112px"
            >
              {screenRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.formattedName}
                </option>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
      )}
      <Box display="table-cell">
        <Button
          size="sm"
          variant="outline"
          leftIcon={<MdDelete />}
          isDisabled={isDisabled}
          onClick={() => {
            remove(idx);
          }}
        >
          Remove
        </Button>
      </Box>
    </Box>
  );
};

export default InviteUsersButton;
